import React from "react";
import { Avatar, Box, Tooltip } from "@mui/material";
import { tokens } from "../../theme";

export interface AvatarStackPerson {
  name: string;
  src?: string;
}

export interface AvatarStackProps {
  people: AvatarStackPerson[];
  /** Max avatars before collapsing into a "+N" bubble. */
  max?: number;
  size?: number;
}

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");

/**
 * Overlapping avatar stack with a "+N" overflow bubble. Replaces the max-2
 * Collaborators rendering (which can delegate here).
 */
export const AvatarStack: React.FC<AvatarStackProps> = ({
  people,
  max = 3,
  size = 30,
}) => {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;

  const bubbleSx = {
    width: size,
    height: size,
    fontSize: size * 0.4,
    fontFamily: tokens.typography.family,
    border: `2px solid ${tokens.color.surface.base}`,
    marginLeft: shown.length > 0 ? "-8px" : 0,
  } as const;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
      {shown.map((p, i) => (
        <Tooltip key={`${p.name}-${i}`} title={p.name}>
          <Avatar
            src={p.src}
            sx={{
              ...bubbleSx,
              marginLeft: i === 0 ? 0 : "-8px",
              bgcolor: tokens.color.surface.raisedHi,
              color: tokens.color.text.body,
            }}
          >
            {!p.src && initials(p.name)}
          </Avatar>
        </Tooltip>
      ))}
      {extra > 0 && (
        <Avatar
          sx={{
            ...bubbleSx,
            bgcolor: tokens.color.surface.raised2,
            color: tokens.color.text.secondary,
          }}
        >
          +{extra}
        </Avatar>
      )}
    </Box>
  );
};

export default AvatarStack;
